import { Layout, Input, Flex, Space, Tooltip, Typography, Button, theme, Form } from "antd";
import { HeaderApp, NavigationBar, useHybridResponsive } from "../../shared";
import { PlusOutlined, UserOutlined, SearchOutlined, FilterOutlined } from "@ant-design/icons";
import HeaderPage from "../../shared/components/HeaderPage";
import EmptyData from "../../shared/components/EmptyData";
import { useFetchClients } from "../hooks/useFetchClients";
import Loading from "../../shared/components/Loading";
import { useNavigate } from "react-router-dom";
import HybridCard from "../../shared/components/HybridCard";
import DrawerDetails from "../../shared/components/DrawerDetails";
import { useState, useEffect, use } from "react";
import type { ClientData } from "../types";
import ClientDetails from "../components/ClientDetails";
import ClientForm from "../components/ClientForm";
import { useEditClient } from "../hooks/useEditClient";
import openNotification from "../../../utils/openNotification";

const Clients: React.FC = () => {
	const { data: clients, isLoading } = useFetchClients();
	const navigate = useNavigate();
	const [selectedClient, setSelectedClient] = useState<ClientData | null>(null);
	const [editClient, setEditClient] = useState<ClientData | null>(null);
	const [searchOpen, setSearchOpen] = useState(false);
	const [searchText, setSearchText] = useState("");
	const { token } = theme.useToken();
	const [form] = Form.useForm();
	const { mutate: editClientMutation, isPending: isEditing } = useEditClient();
	const { isMobile } = useHybridResponsive();

	useEffect(() => {
		if (editClient) {
			form.setFieldsValue(editClient);
		} else {
			form.resetFields();
		}
	}, [editClient, form]);

	const handleEditFinish = async (values: ClientData) => {
		const payload = { ...values, id_client: editClient?.id_client };
		editClientMutation(payload, {
			onSuccess: () => {
				openNotification('success', 'Cliente actualizado exitosamente');
				setEditClient(null);
			},
			onError: (error) => {
				openNotification('error', 'Error', error.message);
			}
		});
	};


	if (isLoading) {
		return <Loading />
	}

	const filteredClients = clients?.filter((c) => {

		if (!searchText) return true;
		const lowerSearch = searchText.toLowerCase();
		return (
			c.client_name.toLowerCase().includes(lowerSearch) ||
			c.client_email.toLowerCase().includes(lowerSearch) ||
			c.client_rfc.toLowerCase().includes(lowerSearch)
		);
	}) || [];

	return (
		<>
			<HeaderApp sectionName="Catalogo | Clientes" showBack />
			<Layout.Content style={{ padding: "16px", width: "100%", backgroundColor: token.colorBgLayout }} className="max-width-container">
				<HeaderPage
					title="Clientes"
					subtitle="Gestionar tus clientes"
					actions={[
						{
							label: "Agregar cliente",
							icon: <PlusOutlined />,
							type: "primary",
							onClick: () => {
								navigate("/catalogo/clientes/nuevo");
							},
						},
					]}
				/>
				{/* List Header */}
				<Flex
					justify="space-between"
					align="center"
					style={{ marginBottom: 12, padding: "0 4px" }}
				>
					<Typography.Text strong style={{ fontSize: 15 }}>
						Listado
					</Typography.Text>
					<Space>
						{searchOpen && (
							<Input
								prefix={<SearchOutlined />}
								placeholder="Buscar cliente..."
								size="small"
								autoFocus
								style={{ borderRadius: 8, width: 200 }}
								value={searchText}
								onChange={(e) => setSearchText(e.target.value)}
								onBlur={() => {
									if (!searchText) setSearchOpen(false);
								}}
							/>
						)}
						<Tooltip title="Filtrar">
							<Button
								type="text"
								icon={<FilterOutlined />}
								shape="default"
								style={{ borderRadius: 8 }}
							/>
						</Tooltip>
						<Tooltip title="Buscar">
							<Button
								type="text"
								icon={<SearchOutlined />}
								shape="default"
								style={{ borderRadius: 8 }}
								onClick={() => setSearchOpen(true)}
							/>
						</Tooltip>
					</Space>
				</Flex>
				<main>
					{filteredClients.length === 0 ?
						<EmptyData
							description="No hay clientes registrados"
						/>
						: filteredClients.map((client) => (
							<HybridCard
								key={client.id_client}
								title={`${client.client_name} ${client.client_lastname_1}`}
								subtitle={`${client.client_email} - ${client.client_type}`}
								description={`${client.client_rfc ? `RFC: ${client.client_rfc}` : "-"} | ${client.client_company_name ? `Empresa: ${client.client_company_name}` : "-"}  ${client.client_cp ? `| C.P.: ${client.client_cp}` : "-"} `}
								icon={<UserOutlined />}
								onClick={() => {
									if (!isMobile)
										setSelectedClient(client)
								}}
								swipe={{
									onView: () => setSelectedClient(client),
									onEdit: () => setEditClient(client),
								}}
							/>
						))}
				</main>
				<DrawerDetails
					title="Detalles cliente"
					onClose={() => {
						setSelectedClient(null)
					}}
					open={!!selectedClient}
					contentBody={
						selectedClient ? <ClientDetails client={selectedClient} /> : null
					}
				/>
				<DrawerDetails
					title="Editar Cliente"
					onClose={() => setEditClient(null)}
					open={!!editClient}
					contentBody={
						editClient ? (
							<div style={{ maxWidth: 640, margin: "0 auto" }}>
								<ClientForm
									form={form}
									onFinish={handleEditFinish}
									isPending={isEditing}
									buttonText="Actualizar Cliente"
									formId="edit-client-form"
								/>
							</div>
						) : null
					}
				/>
				<NavigationBar activeKey="/providers" />
			</Layout.Content>
		</>
	);
};

export default Clients;
